import { useCallback, useEffect, useState, useRef, createRef } from 'react';
import I18n from 'I18n';
import styled from 'styled-components';
import PortalIdParser from 'PortalIdParser';
import Small from 'UIComponents/elements/Small';
import H2 from 'UIComponents/elements/headings/H2';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import ContactNotInSystemSection from './ContactNotInSystemSection';
import ContactInSystemSection from './ContactInSystemSection';
import { usageTracker } from '../../../usage-tracker/usageTracker';
import { getUsageTrackingChannel } from '../../../usage-tracker/trackerChannelIdMapping';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDialogBody = styled(UIDialogBody).withConfig({
  displayName: "ContactPromotionModal__StyledDialogBody",
  componentId: "sc-11uyeyf-0"
})(["padding-top:24px;padding-left:40px;padding-right:40px;"]);
const StyledUIFlex = styled(UIFlex).withConfig({
  displayName: "ContactPromotionModal__StyledUIFlex",
  componentId: "sc-11uyeyf-1"
})(["align-items:center;"]);
const StyledButton = styled(UIButton).withConfig({
  displayName: "ContactPromotionModal__StyledButton",
  componentId: "sc-11uyeyf-2"
})(["margin-top:30px;margin-left:16px;"]);
const StyledUIFormControl = styled(UIFormControl).withConfig({
  displayName: "ContactPromotionModal__StyledUIFormControl",
  componentId: "sc-11uyeyf-3"
})(["width:297px;"]);
const ContactPromotionModal = ({
  contacts,
  isContactsLoading,
  contactsError,
  promoteContactResponse,
  promoteError,
  isPromoteLoading,
  focusedThreadId,
  unknownVisitorVid,
  handleContactLookUp,
  handlePromoteToContact,
  setShowContactPromotionModal,
  genericChannelId
}) => {
  const portalId = PortalIdParser.get();
  const firstRender = useRef(true);
  const emailInputRef = /*#__PURE__*/createRef();
  const [confirmBtnClicked, setConfirmBtnClicked] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [vid, setVid] = useState();
  const [email, setEmail] = useState('');
  const [isContactInSystem, setIsContactInSystem] = useState(true);
  const [isLookUpBttnEnabled, setIsLookUpBttnEnabled] = useState(false);
  const [displaySecondSection, setDisplaySecondSection] = useState(false);
  const handleEmailValidation = useCallback(e => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (e.target) {
      const isValidEmail = e.target.value.match(regex);
      setIsLookUpBttnEnabled(isValidEmail);
    }
  }, [setIsLookUpBttnEnabled]);
  const onLookUp = () => {
    setFirstName('');
    setLastName('');
    usageTracker.track('contact-promotion', {
      action: 'completion of step one in the flow',
      channel: genericChannelId && getUsageTrackingChannel(genericChannelId)
    });
    const emailAddress = emailInputRef.current && emailInputRef.current.value || '';
    setEmail(emailAddress);
    handleContactLookUp({
      email: emailAddress
    });
  };
  const onConfirm = () => {
    if (unknownVisitorVid === undefined) {
      return;
    }
    usageTracker.track('contact-promotion', {
      action: isContactInSystem ? 'completion of step two in the flow - user reviewed existing contact' : 'completion of step two in the flow - new contact created',
      channel: genericChannelId && getUsageTrackingChannel(genericChannelId)
    });
    setConfirmBtnClicked(true);
    handlePromoteToContact({
      firstName,
      lastName,
      email,
      vid: unknownVisitorVid,
      portalId,
      focusedThreadId
    });
  };
  useEffect(() => {
    if (firstRender.current || isContactsLoading) {
      firstRender.current = false;
      return;
    }
    if (contactsError) {
      reportError(contactsError);
      return;
    }
    if (contacts) {
      if (contacts.length > 0) {
        const result = contacts.find(contact => contact.properties.email.value === email);
        if (result) {
          setFirstName(result.properties.firstname && result.properties.firstname.value || '');
          setLastName(result.properties.lastname && result.properties.lastname.value || '');
          setVid(result.vid || undefined);
          setIsContactInSystem(true);
        } else {
          setIsContactInSystem(false);
        }
      } else {
        setIsContactInSystem(false);
      }
      setDisplaySecondSection(true);
    }
  }, [contactsError, isContactsLoading, contacts, email]);
  useEffect(() => {
    if (promoteError) {
      reportError(promoteError);
      return;
    }
    if (isPromoteLoading) {
      return;
    }
    if (promoteContactResponse) {
      setShowContactPromotionModal(false);
    }
  }, [promoteContactResponse, promoteError, isPromoteLoading, setShowContactPromotionModal]);
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "contact-promo-modal",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-message-history.contact-promotion-modal.modalTitle"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: () => setShowContactPromotionModal(false),
        "data-test-id": "contact-promo-modal-close-btn"
      })]
    }), /*#__PURE__*/_jsxs(StyledDialogBody, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-inbox-message-history.contact-promotion-modal.stepOne"
      }), /*#__PURE__*/_jsxs(UIFlex, {
        children: [/*#__PURE__*/_jsx(StyledUIFormControl, {
          label: I18n.text('conversations-inbox-message-history.contact-promotion-modal.emailAddress'),
          error: !isContactInSystem || contactsError,
          validationMessage: !isContactInSystem ? I18n.text('conversations-inbox-message-history.contact-promotion-modal.emailValidationMessage') : contactsError ? I18n.text('conversations-inbox-message-history.contact-promotion-modal.contactsError') : null,
          children: /*#__PURE__*/_jsxs(StyledUIFlex, {
            children: [/*#__PURE__*/_jsx(UITextInput, {
              name: "email-address-input",
              inputRef: emailInputRef,
              onFocus: () => setConfirmBtnClicked(false),
              onChange: handleEmailValidation
            }), isContactsLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
              size: "extra-small"
            })]
          })
        }), /*#__PURE__*/_jsx(UIFormControl, {
          children: /*#__PURE__*/_jsx(StyledButton, {
            use: "link",
            disabled: !isLookUpBttnEnabled,
            onClick: onLookUp,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-inbox-message-history.contact-promotion-modal.lookUpBttn"
            })
          })
        })]
      }), displaySecondSection && !isContactsLoading ? isContactInSystem ? /*#__PURE__*/_jsx(ContactInSystemSection, {
        firstName: firstName,
        lastName: lastName,
        vid: vid,
        email: email
      }) : /*#__PURE__*/_jsx(ContactNotInSystemSection, {
        setFirstName: setFirstName,
        setLastName: setLastName,
        email: email
      }) : null]
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      children: displaySecondSection && email && !isContactsLoading && /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          direction: "row",
          children: [/*#__PURE__*/_jsx(UIButton, {
            use: "primary",
            onClick: onConfirm,
            disabled: isContactInSystem ? false : firstName === '' || lastName === '',
            children: I18n.text('conversations-inbox-message-history.contact-promotion-modal.buttons.confirm')
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => setShowContactPromotionModal(false),
            children: I18n.text('conversations-inbox-message-history.contact-promotion-modal.buttons.cancel')
          }), isPromoteLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
            size: "extra-small"
          })]
        }), promoteError && confirmBtnClicked && /*#__PURE__*/_jsx(Small, {
          "aria-describedby": I18n.text('conversations-inbox-message-history.contact-promotion-modal.promoteError'),
          className: "m-top-2",
          use: "error",
          weight: "bold",
          children: I18n.text('conversations-inbox-message-history.contact-promotion-modal.promoteError')
        })]
      })
    })]
  });
};
ContactPromotionModal.displayName = 'ContactPromotionModal';
export default ContactPromotionModal;